/**
 * Контент. Три вопроса разделены намеренно: что даёт просмотры, что вызывает
 * реакции и что пересылают — это разные публикации, и оптимизировать всё под
 * просмотры значит однажды превратить канал в ленту заголовков.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, type ContentFinding } from "../lib/api";
import { useSession } from "../session";
import {
  Badge,
  Card,
  ConfidenceBadge,
  Delta,
  Empty,
  ErrorBox,
  LoadingDashboard,
  PageHeader,
  Paywall,
  SegmentedControl,
  Why,
} from "../components/ui";
import { BarList, ChartCard, Heatmap } from "../components/charts";
import { WEEKDAYS, auto, decimal, num, plural, signedPct } from "../lib/format";

interface ContentResponse {
  window_days: number;
  maturity_hours: number;
  top: ContentFinding[];
  by_outcome: Record<string, ContentFinding[]>;
  outcomes: Record<string, string>;
  topics: { key: string; title: string; posts: number; aliases: string[] }[];
}

interface TimingResponse {
  cells: { weekday: number; hour: number; posts: number; views_median: number | null; reliable: boolean }[];
  window_days: number;
  total_posts: number;
}

export default function ContentPage() {
  const { channelId } = useSession();
  const [outcome, setOutcome] = useState("views");

  const content = useQuery({
    queryKey: ["content", channelId],
    queryFn: () => api.get<ContentResponse>(`/analytics/${channelId}/content`),
    enabled: Boolean(channelId),
  });
  const timing = useQuery({
    queryKey: ["timing", channelId],
    queryFn: () => api.get<TimingResponse>(`/analytics/${channelId}/timing`),
    enabled: Boolean(channelId),
  });

  if (!channelId) {
    return (
      <Card>
        <Empty title="Сначала подключите канал" actions={<Link to="/channels"><button className="primary">Каналы</button></Link>} />
      </Card>
    );
  }
  if (content.error instanceof ApiError && content.error.isPaywall) {
    return <Paywall what="Разбор содержания" />;
  }
  if (content.isLoading) return <LoadingDashboard />;
  if (content.error) return <ErrorBox error={content.error} />;
  const data = content.data!;
  const rows = data.by_outcome[outcome] ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Детальная аналитика"
        title="Контент"
        lede={`Сравнение идёт внутри сопоставимых групп: рубрика, формат и время суток выровнены — иначе «видео лучше текста» оказалось бы разницей времени выхода. Окно ${data.window_days} дней, дозревание ${data.maturity_hours} ч.`}
      />

      <Card title="На что стоит опереться" note="только надёжные и заметные различия">
        {data.top.length === 0 ? (
          <p className="muted">
            Устойчивых различий не нашлось. Это нормальный ответ: чтобы утверждать, нужно хотя бы
            восемь сопоставимых публикаций в каждой группе.
          </p>
        ) : (
          <div>
            {data.top.map((finding) => (
              <FindingRow key={rowKey(finding)} finding={finding} />
            ))}
          </div>
        )}
      </Card>

      <Card
        title="Разложить по тому, что измеряем"
        note={`${rows.length} ${plural(rows.length, "различие", "различия", "различий")}`}
        actions={
          <SegmentedControl
            value={outcome}
            onChange={setOutcome}
            ariaLabel="Что измеряем"
            options={Object.entries(data.outcomes).map(([value, label]) => ({ value, label }))}
          />
        }
      >
        {rows.length === 0 ? (
          <p className="muted">По этому показателю различий не нашлось.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Признак</th>
                  <th className="num">У него</th>
                  <th className="num">У остальных</th>
                  <th className="num">Разница</th>
                  <th className="num">Публикаций</th>
                  <th>Уверенность</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((finding) => (
                  <tr key={rowKey(finding)}>
                    <td>{finding.level_title}</td>
                    <td className="num mono">{auto(finding.median_level)}</td>
                    <td className="num mono">{auto(finding.median_rest)}</td>
                    <td className="num">
                      <Delta value={finding.delta_pct} />
                    </td>
                    <td className="num dim">
                      {num(finding.n_level)} / {num(finding.n_rest)}
                    </td>
                    <td>
                      <ConfidenceBadge level={finding.confidence} reason={finding.note} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data.topics.length > 0 && (
        <Card
          title="Рубрики канала"
          note="предложены моделью один раз и дальше не переименовываются"
        >
          {/* Столбики, а не облако значков: вопрос здесь «чего больше», а
              одинаковые плашки на него не отвечают. */}
          <BarList
            items={data.topics.slice(0, 12).map((topic) => ({
              label: topic.title,
              value: topic.posts,
              title: topic.aliases.length ? `Также: ${topic.aliases.join(", ")}` : undefined,
            }))}
            formatValue={(value) => `${num(value)} публ.`}
          />
        </Card>
      )}

      <ChartCard
        title="Время выхода"
        sub={
          timing.data
            ? `${num(timing.data.total_posts)} публикаций за ${timing.data.window_days} дней`
            : "Медиана просмотров по дням недели и часам"
        }
        loading={timing.isLoading}
        error={timing.error ?? undefined}
        footnote="Серые клетки — там, где публикаций было меньше четырёх. Именно из таких клеток рождаются советы публиковать в четыре утра."
      >
        {timing.data && timing.data.cells.length > 0 ? (
          <Heatmap cells={timing.data.cells} weekdays={WEEKDAYS} />
        ) : (
          <div className="chart-empty">
            <span>Данных для карты пока нет.</span>
            <span className="faint">Нужны публикации с известными просмотрами за последние недели.</span>
          </div>
        )}
      </ChartCard>
    </>
  );
}

function FindingRow({ finding }: { finding: ContentFinding }) {
  const better = (finding.delta_pct ?? 0) > 0;
  return (
    <div className={`signal ${better ? "info" : "warn"}`}>
      <span className="rail" style={better ? { background: "var(--positive)" } : undefined} />
      <div className="signal-body">
        <div className="what">
          {finding.level_title}: {finding.outcome_title.toLowerCase()} {signedPct(finding.delta_pct)}
        </div>
        <div className="check">{finding.note}</div>
        <div className="meta">
          <ConfidenceBadge level={finding.confidence} />
          <Badge tone="muted">
            {num(finding.n_level)} против {num(finding.n_rest)}
          </Badge>
          {finding.kind === "association" && <Badge tone="warn">связь, не причина</Badge>}
        </div>
        <Why>
          <table className="facts">
            <tbody>
              <tr>
                <td className="dim">Медиана у этого признака</td>
                <td className="mono">{auto(finding.median_level)}</td>
              </tr>
              <tr>
                <td className="dim">Медиана у остальных</td>
                <td className="mono">{auto(finding.median_rest)}</td>
              </tr>
              <tr>
                <td className="dim">Вероятность превосходства</td>
                <td className="mono">
                  {finding.effect === null ? "—" : `${decimal(finding.effect * 100, 0)}%`}
                </td>
              </tr>
              <tr>
                <td className="dim">Вероятность случайности (p)</td>
                <td className="mono">{finding.p_value === null ? "—" : decimal(finding.p_value, 3)}</td>
              </tr>
              <tr>
                <td className="dim">Сопоставимых групп</td>
                <td className="mono">{num(finding.strata_used)}</td>
              </tr>
            </tbody>
          </table>
        </Why>
      </div>
    </div>
  );
}

function rowKey(finding: ContentFinding): string {
  return `${finding.dimension}:${finding.level}:${finding.outcome}`;
}
